// Color cards on the product page (snippets/variant-color-cards.liquid and
// snippets/product-color-cards.liquid). Each list is [data-color-cards] and
// each card .color-cards__item carries its variants in data-variants:
// [{ id, price, available, options: { Size: 'Slim' } }] (options other than
// color). The card URL is data-url.
//
// - Mobile arrow: scrolls the row, hidden at the end.
// - Size changed (theme's variant:change): each card shows the price and
//   availability of its variant in that size, and its URL points to it; the
//   "N Colors Available" count follows that size too.
// - Choosing a color loads another page (a variant or another product): its
//   page is prefetched on hover / touch, and the new page scrolls back to
//   where the cards were on screen.
(() => {
  const SCROLL_KEY = 'color-cards-scroll';

  const formatMoney = (cents) =>
    window.theme && theme.Currency ? theme.Currency.formatMoney(cents) : '$' + (cents / 100).toFixed(2);

  const readJSON = (text, fallback) => {
    try {
      return JSON.parse(text);
    } catch (error) {
      return fallback;
    }
  };

  const saveScroll = (list, url) => {
    try {
      const path = new URL(url, window.location.origin).pathname;
      sessionStorage.setItem(
        SCROLL_KEY,
        JSON.stringify({ path, top: list.getBoundingClientRect().top, time: Date.now() })
      );
    } catch (error) {}
  };

  // Scroll so the list is where it was on the previous page. Returns the
  // scroll position set, or undefined when there was nothing to restore.
  const restoreScroll = (list) => {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY));
    } catch (error) {}
    if (!saved || saved.path !== window.location.pathname || Date.now() - saved.time > 15000) return undefined;
    window.scrollTo(0, list.getBoundingClientRect().top + window.scrollY - saved.top);
    return window.scrollY;
  };

  const prefetched = new Set();
  const prefetch = (url) => {
    if (!url || prefetched.has(url)) return;
    prefetched.add(url);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  };

  const initList = (list) => {
    if (list.dataset.colorCardsReady) return;
    list.dataset.colorCardsReady = 'true';

    const items = Array.from(list.querySelectorAll('.color-cards__item'));
    const optionNames = readJSON(list.dataset.optionNames, []);
    const arrow = list.parentElement.querySelector('[data-color-cards-next]');

    // Mobile arrow.
    let updateArrow = () => {};
    if (arrow) {
      updateArrow = () => {
        const scrollable = list.scrollWidth - list.clientWidth > 1;
        arrow.hidden = !scrollable || list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
      };
      arrow.addEventListener('click', () => {
        const visible = items.find((item) => item.offsetWidth > 0);
        const step = visible ? visible.offsetWidth * 2 : list.clientWidth / 2;
        list.scrollBy({ left: step, behavior: 'smooth' });
      });
      list.addEventListener('scroll', updateArrow, { passive: true });
      window.addEventListener('resize', updateArrow);
      window.addEventListener('load', updateArrow);

      // Keep the selected color in view.
      const selected = list.querySelector('input:checked, .color-cards__item--current');
      if (selected) {
        const item = selected.closest('.color-cards__item');
        list.scrollLeft = Math.max(0, item.offsetLeft - list.offsetLeft - item.offsetWidth);
      }
      updateArrow();
    }

    // Back to the same place after choosing a color; again on load (images
    // above may change the height), unless the visitor already scrolled.
    const restoredY = restoreScroll(list);
    if (restoredY !== undefined) {
      window.addEventListener('load', () => {
        if (Math.abs(window.scrollY - restoredY) < 5) restoreScroll(list);
        try {
          sessionStorage.removeItem(SCROLL_KEY);
        } catch (error) {}
      });
    }

    // Prefetch on hover, touch or keyboard focus.
    const onIntent = (event) => {
      const item = event.target.closest && event.target.closest('.color-cards__item');
      if (!item || item.classList.contains('color-cards__item--current')) return;
      if (item.querySelector('input:checked')) return;
      prefetch(item.dataset.url);
    };
    list.addEventListener('pointerover', onIntent, { passive: true });
    list.addEventListener('touchstart', onIntent, { passive: true });
    list.addEventListener('focusin', onIntent);

    // Leaving the page: color radio (the theme reloads with the variant) or
    // product link.
    list.addEventListener('change', (event) => {
      const item = event.target.closest('.color-cards__item');
      if (item) saveScroll(list, item.dataset.url || window.location.href);
    });
    list.addEventListener('click', (event) => {
      const link = event.target.closest('a.color-cards__card');
      if (link) saveScroll(list, link.href);
    });

    // Size changed: price, availability and URL of each card.
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant || !Array.isArray(variant.options)) return;
      const selected = {};
      optionNames.forEach((name, index) => {
        selected[name] = variant.options[index];
      });

      items.forEach((item) => {
        const variants = readJSON(item.dataset.variants, []);
        const match = variants.find((candidate) =>
          Object.keys(candidate.options || {}).every(
            (name) => selected[name] === undefined || selected[name] === candidate.options[name]
          )
        );
        if (!match) return;
        const price = item.querySelector('[data-color-card-price]');
        if (price) price.innerHTML = formatMoney(match.price);
        item.classList.toggle('color-cards__item--unavailable', !match.available);

        const url = new URL(item.dataset.url || window.location.href, window.location.origin);
        url.searchParams.set('variant', match.id);
        item.dataset.url = url.pathname + url.search;
        const link = item.querySelector('a.color-cards__card');
        if (link) link.href = item.dataset.url;
      });

      // "N Colors Available" counts the colors available in this size.
      const wrapper = list.closest('.color-cards');
      const count = wrapper && wrapper.querySelector('[data-color-cards-count]');
      if (count) {
        const available = items.filter((item) => !item.classList.contains('color-cards__item--unavailable')).length;
        count.textContent = available;
        const noun = wrapper.querySelector('[data-color-cards-noun]');
        if (noun) {
          const singular = wrapper.dataset.nounSingular || 'Color';
          const plural = wrapper.dataset.nounPlural || 'Colors';
          noun.textContent = available === 1 ? singular : plural;
        }
      }

      // Sold out cards are hidden, so the row may have changed length.
      updateArrow();
    });
  };

  const init = () => document.querySelectorAll('[data-color-cards]').forEach(initList);

  // Dropdown-group cards are fetched and inserted after the page's initial
  // DOM scan. Initialize that list as soon as its markup is ready.
  document.addEventListener('color-cards:ready', (event) => {
    const list = event.detail && event.detail.list;
    if (list) initList(list);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
